import { observer } from "mobx-react-lite";
import { useCallback, useState } from "react";
import { useAppStore } from "@/store";
import { MDSButton, MDSButtonVariant } from "@/mds";
import { YesNoInput } from "@/components/active-session/input-forms/components/yes-no-input";
import {
  FormContainer,
  FormBody,
  ButtonRow,
  FormLabel,
  FormItem,
} from "@/components/active-session/input-forms/input-form.styles";
import { JsonObject } from "@/domains/common/types";

interface ImportMarkdownFormProps {
  requestId: string;
  sessionId: string;
  dismissImportSession: () => void;
  metadata: JsonObject;
}

export const ImportMarkdownForm: React.FC<ImportMarkdownFormProps> = observer(
  ({ requestId, sessionId, dismissImportSession, metadata }) => {
    const { store } = useAppStore();
    const [createFromHashtags, setCreateFromHashtags] = useState(true);
    const [useFilenamesAsTitles, setUseFilenamesAsTitles] = useState(false);

    const handleCancel = useCallback(() => {
      dismissImportSession();
    }, [dismissImportSession]);

    const handleSubmit = useCallback(
      async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const config = {
          create_collections_from_tags: createFromHashtags,
          use_filenames_as_titles: useFilenamesAsTitles,
        };

        // respond to the input request
        await store.sessionEvents.createInputResponseSessionEvent({
          sessionId,
          config,
          requestId,
          metadata,
          inputKind: "UPLOADED_FILE_BATCH_CONFIG",
        });

        // TODO: this is kinda "faking" it. Instead of sending a message on behalf of Mem from the frontend,
        // we should include the filled out form in the Session and a user-side message.
        const settingsMessage =
          config.create_collections_from_tags && config.use_filenames_as_titles
            ? "Creating collections from hashtags and using filenames as titles."
            : config.create_collections_from_tags
              ? "Creating collections from hashtags."
              : config.use_filenames_as_titles
                ? "Using filenames as titles."
                : "";

        // TODO: Improve the way this message is displayed
        store.sessionEvents.createMessageSentSessionEvent({
          session_id: sessionId,
          message_value: {
            kind: "COMMUNICATION",
            content: `I received your settings. ${settingsMessage}`,
          },
        });
      },
      [
        createFromHashtags,
        useFilenamesAsTitles,
        requestId,
        sessionId,
        store.sessionEvents,
        metadata,
      ]
    );

    return (
      <FormContainer onSubmit={handleSubmit}>
        <FormBody>
          <FormItem>
            <FormLabel htmlFor="create_collections_from_tags">
              Do you want to convert all inline hashtags to collections?
            </FormLabel>
            <YesNoInput value={createFromHashtags} onChange={setCreateFromHashtags} />
          </FormItem>
          <FormItem>
            <FormLabel htmlFor="use_filenames_as_titles">Use filenames as titles?</FormLabel>
            <YesNoInput value={useFilenamesAsTitles} onChange={setUseFilenamesAsTitles} />
          </FormItem>
          <ButtonRow>
            <MDSButton onClick={handleCancel}>Cancel</MDSButton>
            <MDSButton variant={MDSButtonVariant.Contrast} type="submit">
              Submit
            </MDSButton>
          </ButtonRow>
        </FormBody>
      </FormContainer>
    );
  }
);

ImportMarkdownForm.displayName = "ImportMarkdownForm";
